/**
 * Create / Update self-schedule mutations.
 * POST /api_ats/v1/ats_schedules
 * PUT  /api_ats/v1/ats_schedules/:id
 *
 * Self-schedule payloads use FormData (file attachments).
 * Payload shape will be finalised — keep loose typing for now.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { asAtsClientBody, unwrapAtsEnvelope } from '@/api/ats/atsEnvelope';
import { convertToFormData } from '@/lib/util';

export interface CreateSelfScheduleVariables {
  payload: Record<string, unknown>;
}

export interface UpdateSelfScheduleVariables {
  scheduleId: number;
  payload: Record<string, unknown>;
}

export function useCreateSelfSchedule() {
  const queryClient = useQueryClient();

  return useMutation<
    Record<string, unknown>,
    Error,
    CreateSelfScheduleVariables
  >({
    mutationFn: async ({ payload }) => {
      const raw = asAtsClientBody<unknown>(
        await http.post('/ats_schedules', convertToFormData(payload))
      );
      return unwrapAtsEnvelope<Record<string, unknown>>(raw);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-interview'] });
    },
  });
}

export function useUpdateSelfSchedule() {
  const queryClient = useQueryClient();

  return useMutation<
    Record<string, unknown>,
    Error,
    UpdateSelfScheduleVariables
  >({
    mutationFn: async ({ scheduleId, payload }) => {
      const raw = asAtsClientBody<unknown>(
        await http.put(
          `/ats_schedules/${scheduleId}`,
          convertToFormData(payload)
        )
      );
      return unwrapAtsEnvelope<Record<string, unknown>>(raw);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-interview'] });
    },
  });
}
